// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminSidebar from './components/AdminSidebar';
import api from '../../services/api'; // Import the API service
import '../../styles/admin.css';

function AdminDashboard() {
  const [stats, setStats] = useState({
    deviceCount: 0,
    repairCount: 0,
    categoryCount: 0,
    brandCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          navigate('/admin/login');
          return;
        }
        
        // Fetch devices directly like Sanjay's version
        const response = await fetch('/api/admin/devices', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('adminToken');
            navigate('/admin/login');
            return;
          }
          throw new Error('Failed to fetch devices');
        }
        
        const devices = await response.json();
        
        // Calculate stats from devices
        let totalRepairs = 0;
        const brands = new Set();
        const deviceTypes = new Set();
        
        devices.forEach(device => {
          totalRepairs += (device.repairs?.length || 0);
          if (device.brand) brands.add(device.brand);
          if (device.type) deviceTypes.add(device.type);
        });
        
        setStats({
          deviceCount: devices.length,
          repairCount: totalRepairs,
          categoryCount: deviceTypes.size,
          brandCount: brands.size
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [navigate]);
  
  return (
    <div className="admin-layout">
      <AdminSidebar activeItem="dashboard" />
      
      <div className="admin-content">
        <div className="admin-header">
          <h1>Dashboard</h1>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {loading ? (
          <div className="loading">Loading dashboard data...</div>
        ) : (
          <>
            <div className="dashboard-stats">
              <div className="stat-card">
                <h3>Total Devices</h3>
                <div className="stat-number">{stats.deviceCount}</div>
              </div>
              
              <div className="stat-card">
                <h3>Total Repairs</h3>
                <div className="stat-number">{stats.repairCount}</div>
              </div>
              
              <div className="stat-card">
                <h3>Categories</h3>
                <div className="stat-number">{stats.categoryCount}</div>
              </div>
            </div>
            
            <div className="admin-welcome">
              <h2>Welcome to your Device Repair Admin Panel</h2>
              <p>From here, you can manage all aspects of your repair business:</p>
              <ul>
                <li>Add, edit, or remove devices</li>
                <li>Update repair types and pricing</li>
                <li>Manage device categories</li>
                <li>Upload new device images</li>
              </ul>
              <p>Use the navigation menu on the left to get started.</p>
              
              <div className="quick-links">
                <h3>Quick Links</h3>
                <div className="quick-links-grid">
                  <Link to="/admin/devices" className="quick-link-card">
                    <div className="quick-link-icon">📱</div>
                    <div className="quick-link-text">Manage Devices</div>
                  </Link>
                  
                  <Link to="/admin/repairs" className="quick-link-card">
                    <div className="quick-link-icon">🔧</div>
                    <div className="quick-link-text">Manage Repairs</div>
                  </Link>
                  
                  <Link to="/admin/analytics" className="quick-link-card">
                    <div className="quick-link-icon">📊</div>
                    <div className="quick-link-text">Analytics</div>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;